<template>
  <div class="ai-response">
    <div class="ai-response-header">
      <AiAvatar />
      <div class="ai-message-container">
        <div class="ai-message">
          <div class="message-content">
            {{ message.text }}
          </div>
          <div class="ai-response-footer">
            <span class="message-time">{{ formattedTime }}</span>
            <div class="rating-icons">
              <MsgPopover ref="popoverUpRef">
                <template #trigger>
                  <button
                    class="rating-button"
                    :class="{ active: rating === 'up' }"
                    aria-label="Rate response positively"
                    @click="handleRating('up')"
                  >
                    <Icon name="lucide:thumbs-up" />
                  </button>
                </template>
                <ResponseFeedbackPopup
                  @share-more-feedback="handleShareMoreFeedback()"
                  @close="popoverUpRef?.hide()"
                />
              </MsgPopover>
              <MsgPopover ref="popoverDownRef">
                <template #trigger>
                  <button
                    class="rating-button"
                    :class="{ active: rating === 'down' }"
                    aria-label="Rate response negatively"
                    @click="handleRating('down')"
                  >
                    <Icon name="lucide:thumbs-down" />
                  </button>
                </template>
                <ResponseFeedbackPopup
                  @share-more-feedback="handleShareMoreFeedback()"
                  @close="popoverDownRef?.hide()"
                />
              </MsgPopover>
              <MsgModal
                v-model="isDetailedFeedbackModalOpen"
                title="Share some details"
                :show-close-button="false"
              >
                <DetailedFeedbackModal
                  @submit="handleSubmitDetailedFeedback()"
                  @cancel="isDetailedFeedbackModalOpen = false"
                />
              </MsgModal>
              <MsgModal
                v-model="isThankForFeedbackModalOpen"
                title="Thank you for your feedback"
                size="medium"
              >
                <ThankForFeedbackModal
                  @close="isThankForFeedbackModalOpen = false"
                />
              </MsgModal>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, useTemplateRef } from "vue";
import AiAvatar from "./AiAvatar.vue";
import ResponseFeedbackPopup from "./ResponseFeedbackPopup.vue";
import DetailedFeedbackModal from "./DetailedFeedbackModal.vue";
import ThankForFeedbackModal from "./ThankForFeedbackModal.vue";
import MsgPopover from "../../MsgUi/MsgPopover.vue";
import MsgModal from "../../MsgUi/MsgModal.vue";
const props = defineProps({
  message: { type: Object, required: true }
});
const emit = defineEmits(["rate"]);
const rating = ref(null);
const isDetailedFeedbackModalOpen = ref(false);
const isThankForFeedbackModalOpen = ref(false);
const popoverUpRef = useTemplateRef("popoverUpRef");
const popoverDownRef = useTemplateRef("popoverDownRef");
const formattedTime = computed(() => {
  const date = typeof props.message.created_at === "string" ? new Date(props.message.created_at) : props.message.created_at;
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
});
function handleRating(newRating) {
  rating.value = rating.value === newRating ? null : newRating;
  if (rating.value) {
    emit("rate", rating.value);
  }
}
function handleShareMoreFeedback() {
  isDetailedFeedbackModalOpen.value = true;
  popoverUpRef.value?.hide();
}
function handleSubmitDetailedFeedback() {
  isThankForFeedbackModalOpen.value = true;
  isDetailedFeedbackModalOpen.value = false;
}
</script>

<style scoped>
.ai-response{flex-direction:column;gap:8px;max-width:80%}.ai-response,.ai-response-header{align-items:flex-start;display:flex}.ai-response-header{gap:12px;width:100%}.ai-message-container{flex:1}.ai-message{display:flex;flex-direction:column;gap:8px}.message-content{background:#fff;border:1px solid #e5e7eb;border-radius:8px;box-shadow:0 1px 2px 0 rgba(0,0,0,.05);color:#374151;line-height:1.5;padding:16px;white-space:pre-wrap}.ai-response-footer{align-items:center;display:flex;justify-content:space-between;padding:0 4px;width:100%}.message-time{color:#6b7280;flex:1;font-size:12px}.rating-icons{display:flex;gap:4px;position:relative}.rating-button{align-items:center;background:transparent;border:none;border-radius:4px;color:#9ca3af;cursor:pointer;display:flex;font-size:14px;height:28px;justify-content:center;transition:all .2s;width:28px}.rating-button:hover{background:#f3f4f6;color:#6b7280}.rating-button.active{color:#6366f1}.rating-button:focus{outline:2px solid #6366f1;outline-offset:2px}
</style>
